/**
 * Canonical low-level source-text scan primitives used by both the
 * `sql-grammar` document layer (scanner, editorContextProjection) and the
 * `intellisense` context layer (tokenizer, statementBoundaries, dottedBase).
 *
 * All functions are pure character-classification and forward-scan helpers —
 * no allocations, no tree-sitter dependency. They take half-open `[from, to)`
 * ranges and return the next index after the construct they consume so callers
 * can chain them in tight loops.
 *
 * `intellisense/context/sourceScan.ts` is a thin re-export of this file
 * (ROUND2-I6). The previous duplication with a "KEEP IN SYNC" comment has
 * been eliminated.
 */

export function isWhitespace(c: number): boolean {
  return c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d;
}

// Includes `@` and `#` so MSSQL `@var` / `#tmp` survive identifier scans.
export function isBareIdentChar(c: number): boolean {
  if (c === 0x5f || c === 0x24 || c === 0x40 || c === 0x23) return true;
  if (c >= 0x30 && c <= 0x39) return true;
  if (c >= 0x41 && c <= 0x5a) return true;
  if (c >= 0x61 && c <= 0x7a) return true;
  return false;
}

export function isBareIdentStart(c: number): boolean {
  if (c === 0x5f || c === 0x40 || c === 0x23) return true;
  if (c >= 0x41 && c <= 0x5a) return true;
  if (c >= 0x61 && c <= 0x7a) return true;
  return false;
}

export function isOperatorChar(c: number): boolean {
  return (
    c === 0x3d || c === 0x3c || c === 0x3e || c === 0x21 ||
    c === 0x2b || c === 0x2d || c === 0x2a || c === 0x2f ||
    c === 0x25 || c === 0x7c || c === 0x26 || c === 0x5e ||
    c === 0x7e || c === 0x3f || c === 0x3a
  );
}

export function skipToEol(source: string, from: number, to: number = source.length): number {
  let i = from;
  while (i < to) {
    const c = source.charCodeAt(i);
    if (c === 0x0a) return i + 1;
    if (c === 0x0d) {
      if (source.charCodeAt(i + 1) === 0x0a) return i + 2;
      return i + 1;
    }
    i += 1;
  }
  return to;
}

export function skipBlockComment(source: string, from: number, to: number = source.length): number {
  let i = from;
  while (i < to - 1) {
    if (source.charCodeAt(i) === 0x2a && source.charCodeAt(i + 1) === 0x2f) {
      return i + 2;
    }
    i += 1;
  }
  return to;
}

export function skipSingleQuoted(source: string, from: number, to: number = source.length): number {
  let i = from;
  while (i < to) {
    if (source.charCodeAt(i) === 0x27) {
      if (source.charCodeAt(i + 1) === 0x27) { i += 2; continue; }
      return i + 1;
    }
    i += 1;
  }
  return to;
}

export function tryDollarQuoted(source: string, from: number, to: number = source.length): number {
  let i = from + 1;
  while (i < to) {
    const c = source.charCodeAt(i);
    if (c === 0x24) break;
    if (!isDollarTagChar(c, i === from + 1)) return -1;
    i += 1;
  }
  if (i >= to || source.charCodeAt(i) !== 0x24) return -1;
  const openEnd = i + 1;
  const tag = source.slice(from, openEnd);
  const closeAt = source.indexOf(tag, openEnd);
  if (closeAt === -1 || closeAt + tag.length > to) return to;
  return closeAt + tag.length;
}

function isDollarTagChar(c: number, first: boolean): boolean {
  if (c === 0x5f) return true;
  if (c >= 0x41 && c <= 0x5a) return true;
  if (c >= 0x61 && c <= 0x7a) return true;
  if (!first && c >= 0x30 && c <= 0x39) return true;
  return false;
}

export function skipBracketedId(source: string, from: number, to: number = source.length): number {
  let i = from;
  while (i < to) {
    if (source.charCodeAt(i) === 0x5d) {
      if (source.charCodeAt(i + 1) === 0x5d) { i += 2; continue; }
      return i + 1;
    }
    i += 1;
  }
  return to;
}

export function skipBacktickId(source: string, from: number, to: number = source.length): number {
  let i = from;
  while (i < to) {
    if (source.charCodeAt(i) === 0x60) {
      if (source.charCodeAt(i + 1) === 0x60) { i += 2; continue; }
      return i + 1;
    }
    i += 1;
  }
  return to;
}

export function skipDoubleQuoted(source: string, from: number, to: number = source.length): number {
  let i = from;
  while (i < to) {
    if (source.charCodeAt(i) === 0x22) {
      if (source.charCodeAt(i + 1) === 0x22) { i += 2; continue; }
      return i + 1;
    }
    i += 1;
  }
  return to;
}
